// Rodeo Level: a lane-based auto-runner. The chosen character rides a bull that
// gallops nonstop to the right through one long rodeo arena. The rider moves
// UP / DOWN across 5 dirt lanes (starts centered, 2 up + 2 down) to dodge farm
// obstacles and line up coins, and can JUMP to hop over things in their lane.
// Reaching the finish flows into the Bull Riding bonus. Speed + lives scale
// with difficulty.
import { GAME } from "./config";
import { ready } from "./assets";
import { drawBullRider } from "./rider";

const { WIDTH, HEIGHT } = GAME;
const STEP_MS = 1000 / 60; // fixed simulation timestep (frame-rate independent movement)
const PLAYER_X = 300;
const GRAVITY = 0.82;
const JUMP_V = 17.5;
const JUMP_CLEAR = 30;            // jumpH above which you clear an obstacle (lower = more forgiving)
const COURSE_LEN = 24000;
// Boss gauntlet at the finish line: 3 distinct bosses, each with its own look
// and attack — El Toro Campeón (charge), El Ganadero (lasso), La Gigantona (chancletas).
const BOSS_PLAN = [
  { type: "charge", n: 3 },
  { type: "lasso", n: 3 },
  { type: "chancleta", n: 3 },
  { type: "clown", n: 3 },
  { type: "fire", n: 4 },   // EL TORO MECÁNICO — climactic final phase (faster, 2-lane sweeps)
];
const BOSS_NAMES = { charge: "EL TORO CAMPEÓN", lasso: "EL GANADERO", chancleta: "LA BORRACHITA", clown: "EL PAYASO DEL RODEO", fire: "EL TORO MECÁNICO" };
const LANE_B = [332, 372, 412, 453, 495, 538]; // lane boundaries (5 lanes + edges)
const STAMP_WARN = 72;           // frames of "¡ESTAMPIDA!" warning before bulls sweep in

// 5 dirt lanes (feet Y + depth scale). Index 2 = center start.
const LANE_Y = [352, 392, 432, 474, 516];
const LANE_SCALE = [0.82, 0.9, 1.0, 1.1, 1.2];

const OBS = [
  { sprite: "obs_hay", w: 92 },
  { sprite: "obs_goat", w: 112 },
  { sprite: "obs_chicken", w: 80 },
  { sprite: "obs_trough", w: 122 },
  { sprite: "obs_fence", w: 116 },
  { sprite: "obs_horse", w: 180 },
  { sprite: "obs_moto", w: 190 },
  { sprite: "obs_mariachi", w: 210 },
];

export default class RodeoLevel {
  constructor(canvas, { character, difficulty, assets, audio, callbacks }) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.ctx.imageSmoothingEnabled = true;
    this.char = character;
    this.diff = difficulty || { id: "normal", lives: 3 };
    this.assets = assets || {};
    this.audio = audio || null;
    this.cb = callbacks || {};
    this.speed = this.diff.id === "facil" ? 5.6 : this.diff.id === "extremo" ? 9.2 : 7.2;
    this.running = false;
    this.initState();
    this.buildCourse();
    this.buildClouds();
    this.buildBoss();
    this.bindKeys();
  }

  initState() {
    this.ended = false;
    this.won = false;
    this.paused = true;
    this.frame = 0;
    this.distance = 0;
    this.lane = 2;
    this.laneYCur = LANE_Y[2];
    this.jumpH = 0;
    this.vVert = 0;
    this.onGround = true;
    this.lives = this.diff.lives || 3;
    this.coins = 0;
    this.invuln = 0;
    this.dust = [];
    this.sparks = [];
    this.wind = [];
    this.hitFlash = 0;
    this.mode = "run"; // run | boss
    this.bossBanner = 0;
    this.stampede = null;
    this.stampBanner = 0;
    this.penalty = 0;
    this.penaltyAmt = 0;
    this.lang = (typeof localStorage !== "undefined" && localStorage.getItem("nicabros_lang")) || "es";
  }

  buildCourse() {
    this.obstacles = [];
    this.coinItems = [];
    this.flags = [];
    let fx = 700, fi = 0;
    while (fx < COURSE_LEN) {
      this.flags.push({ x: fx, fsln: fi % 4 === 3, ph: Math.random() * 6.28 });
      fx += 1500 + Math.random() * 900; fi++;
    }
    const gap = this.diff.id === "extremo" ? 480 : this.diff.id === "facil" ? 760 : 600;
    const blockCount = this.diff.id === "extremo" ? 2 : 1; // lanes blocked per cluster
    let x = 1200, i = 0;
    while (x < COURSE_LEN - 900) {
      // pick lane(s) to block, always leaving open lanes to move into
      const lanes = [0, 1, 2, 3, 4];
      for (let s = lanes.length - 1; s > 0; s--) { const j = Math.floor(Math.random() * (s + 1)); [lanes[s], lanes[j]] = [lanes[j], lanes[s]]; }
      const blocked = lanes.slice(0, blockCount);
      for (const ln of blocked) {
        const o = OBS[Math.floor(Math.random() * OBS.length)];
        this.obstacles.push({ x, lane: ln, sprite: o.sprite, w: o.w, ph: Math.random() * 6.28 });
      }
      // coins: a short row in an OPEN lane leading through the gap
      const openLanes = [0, 1, 2, 3, 4].filter((l) => !blocked.includes(l));
      const cl = openLanes[Math.floor(Math.random() * openLanes.length)];
      const n = 4 + Math.floor(Math.random() * 3);
      for (let k = 0; k < n; k++) this.coinItems.push({ x: x - 220 + k * 48, lane: cl, taken: false });
      x += gap + Math.random() * 260; i++;
    }
    // exactly 3 stampedes: beginning, middle, and before the end
    this.stampIdx = 0;
    this.stampAt = [Math.round(COURSE_LEN * 0.16), Math.round(COURSE_LEN * 0.5), Math.round(COURSE_LEN * 0.83)];
  }

  buildClouds() {
    this.clouds = [];
    for (let i = 0; i < 7; i++) {
      this.clouds.push({ x: Math.random() * WIDTH * 1.4, y: 8 + Math.random() * 30, s: 0.55 + Math.random() * 0.55, spd: 0.18 + Math.random() * 0.32 });
    }
  }

  buildBoss() {
    const list = [];
    for (const ph of BOSS_PLAN) for (let k = 0; k < ph.n; k++) list.push({ type: ph.type });
    this.boss = { hp: 1, x: WIDTH + 320, y: LANE_Y[2], idx: 0, done: 0, total: list.length, attacks: list, state: "idle", timer: 0, cur: null, curHit: false, lastType: null };
  }

  bindKeys() {
    this._onKey = (e, down) => {
      const c = e.code;
      if (c === "Space" || c === "KeyJ") { if (down) this.doJump(); e.preventDefault(); }
      else if (c === "ArrowUp" || c === "KeyW") { if (down) this.moveLane(-1); e.preventDefault(); }
      else if (c === "ArrowDown" || c === "KeyS") { if (down) this.moveLane(1); e.preventDefault(); }
    };
    this._kd = (e) => this._onKey(e, true);
    this._ku = (e) => this._onKey(e, false);
    window.addEventListener("keydown", this._kd);
    window.addEventListener("keyup", this._ku);
  }
  setInput(name) {
    if (name === "up") this.moveLane(-1);
    else if (name === "down") this.moveLane(1);
    else if (name === "jump") this.doJump();
  }
  moveLane(d) {
    if (this.paused || this.ended) return;
    const nl = Math.max(0, Math.min(4, this.lane + d));
    if (nl !== this.lane) { this.lane = nl; this.audio?.sfx("pop"); }
  }
  doJump() {
    if (this.paused || this.ended) return;
    if (this.onGround) { this.vVert = JUMP_V; this.onGround = false; this.audio?.sfx("jump"); }
  }

  begin() { this.paused = false; this.audio?.resume(); }

  start() {
    if (this.running) return;
    this.running = true;
    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);
  }
  destroy() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    window.removeEventListener("keydown", this._kd);
    window.removeEventListener("keyup", this._ku);
  }
  loop() {
    if (!this.running) return;
    const now = performance.now();
    if (!this.ended && !this.paused) {
      // Fixed-timestep accumulator: keeps game speed consistent regardless of
      // device frame rate (slow phones no longer run in slow-motion).
      let frame = this._lastTs != null ? now - this._lastTs : STEP_MS;
      if (frame > 250) frame = 250; // avoid spiral-of-death after tab sleep
      this._acc = (this._acc || 0) + frame;
      let steps = 0;
      while (this._acc >= STEP_MS && steps < 5) { this.update(); this._acc -= STEP_MS; steps++; }
    }
    this._lastTs = now;
    this.render();
    this.raf = requestAnimationFrame(this.loop);
  }

  endWin() {
    if (this.ended) return;
    this.ended = true; this.won = true; this.audio?.sfx("win");
    this.cb.onComplete && this.cb.onComplete({ coins: this.coins, won: true, boss: !!this.bossWin });
  }
  endLose() {
    if (this.ended) return;
    this.ended = true; this.won = false; this.audio?.sfx("gameover");
    this.cb.onGameOver && this.cb.onGameOver({ coins: this.coins, won: false });
  }
  hit() {
    if (this.invuln > 0) return;
    this.lives--; this.invuln = 90; this.hitFlash = 12; this.audio?.sfx("hurt");
    this.cb.onLives && this.cb.onLives(this.lives);
    // penalty: getting hit costs you córdobas, so a clean run scores higher
    if (this.coins > 0) {
      const loss = Math.min(this.coins, 10);
      this.coins -= loss;
      this.penalty = 45;
      this.penaltyAmt = loss;
      this.cb.onCoins && this.cb.onCoins(this.coins);
    }
    if (this.lives <= 0) this.endLose();
  }

  startStampede() {
    const safe = Math.floor(Math.random() * 5);
    this.stampede = { phase: "warn", t: 0, safe, bulls: [] };
    this.stampBanner = STAMP_WARN + 24;
    this.audio?.sfx("pop");
  }

  updateStampede() {
    const s = this.stampede;
    s.t++;
    if (s.phase === "warn") {
      if (s.t === Math.floor(STAMP_WARN * 0.55)) this.audio?.sfx("pop");
      if (s.t >= STAMP_WARN) {
        s.phase = "run"; s.t = 0;
        for (let ln = 0; ln < 5; ln++) {
          if (ln === s.safe) continue;
          s.bulls.push({ lane: ln, x: WIDTH + 150 + Math.random() * 300, kind: ln % 2, hitDone: false });
        }
      }
      return;
    }
    const sweep = this.speed * 1.05 + 4;
    let alive = false;
    for (const bl of s.bulls) {
      bl.x -= sweep;
      if (bl.x > -340) alive = true;
      // safe either in the open lane OR by jumping the bull over the herd
      if (!bl.hitDone && this.invuln === 0 && bl.lane === this.lane && this.jumpH < JUMP_CLEAR && Math.abs(bl.x - PLAYER_X) < 95) {
        bl.hitDone = true; this.hit();
      }
    }
    if (!alive) this.stampede = null;
  }

  update() {
    this.frame++;
    if (this.invuln > 0) this.invuln--;
    if (this.hitFlash > 0) this.hitFlash--;
    if (this.penalty > 0) this.penalty--;
    if (this.bossBanner > 0) this.bossBanner--;
    if (this.stampBanner > 0) this.stampBanner--;

    // smooth lane glide + jump physics (both run and boss modes)
    this.laneYCur += (LANE_Y[this.lane] - this.laneYCur) * 0.25;
    if (!this.onGround) {
      this.jumpH += this.vVert; this.vVert -= GRAVITY;
      if (this.jumpH <= 0) { this.jumpH = 0; this.onGround = true; this.vVert = 0; }
    }

    // hoof dust
    if (this.onGround && this.frame % 3 === 0) this.dust.push({ x: PLAYER_X - 60 + Math.random() * 40, y: this.laneYCur + 16, life: 20, size: 5 + Math.random() * 6 });
    for (const d of this.dust) { d.x -= this.speed * 0.6; d.life--; }
    this.dust = this.dust.filter((d) => d.life > 0);
    for (const s of this.sparks) { s.x += s.vx; s.y += s.vy; s.vy += 0.25; s.life--; }
    this.sparks = this.sparks.filter((s) => s.life > 0);

    // wind streaks
    if (this.frame % 2 === 0) {
      const overDirt = Math.random() < 0.7;
      const y = overDirt ? 305 + Math.random() * (HEIGHT - 320) : 40 + Math.random() * 240;
      this.wind.push({ x: WIDTH + 30, y, len: 24 + Math.random() * 60, vx: -(this.speed * 1.7 + 3 + Math.random() * 4), a: 0.15 + Math.random() * 0.28, dirt: overDirt, life: 90 });
    }
    for (const w of this.wind) { w.x += w.vx; w.life--; }
    this.wind = this.wind.filter((w) => w.x > -80 && w.life > 0);

    if (this.mode === "victory") { this.updateVictory(); return; }
    if (this.mode === "boss") {
      this.updateBoss();
      this.cb.onProgress && this.cb.onProgress({ pct: 100, coins: this.coins, lives: this.lives, boss: true });
      return;
    }

    this.distance += this.speed;

    // Stampede events: a wave of loose bulls thunders down every lane but one.
    if (!this.stampede && this.stampIdx < this.stampAt.length && this.distance >= this.stampAt[this.stampIdx]) {
      this.stampIdx++;
      this.startStampede();
    }
    if (this.stampede) this.updateStampede();

    // collisions (same lane, unless jumping over)
    for (const o of this.obstacles) {
      if (o.done) continue;
      const sx = o.x - this.distance + Math.sin(this.frame * 0.05 + (o.ph || 0)) * 12;
      if (sx + o.w < -40) { o.done = true; continue; }
      const half = o.w * 0.5 * LANE_SCALE[o.lane];
      if (Math.abs(sx - PLAYER_X) < half + 34 && o.lane === this.lane && this.jumpH < JUMP_CLEAR) {
        this.hit(); o.done = true;
      }
    }
    // coins
    for (const c of this.coinItems) {
      if (c.taken) continue;
      const sx = c.x - this.distance;
      if (sx < PLAYER_X - 98 || sx > PLAYER_X + 98) continue;
      const laneMatch = c.lane === this.lane || Math.abs(LANE_Y[c.lane] - this.laneYCur) < 26;
      if (laneMatch) {
        c.taken = true; this.coins++; this.audio?.sfx("coin");
        this.cb.onCoins && this.cb.onCoins(this.coins);
        const cy = LANE_Y[c.lane] - 40;
        for (let k = 0; k < 6; k++) { const a = Math.random() * 6.28, sp = 1 + Math.random() * 3; this.sparks.push({ x: sx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 2, life: 20, color: "#ffd24a" }); }
      }
    }

    this.cb.onProgress && this.cb.onProgress({ pct: Math.min(99, (this.distance / COURSE_LEN) * 100), coins: this.coins, lives: this.lives });
    if (this.distance >= COURSE_LEN) { this.distance = COURSE_LEN; this.enterBoss(); }
  }

  enterBoss() {
    if (this.mode === "boss") return;
    this.mode = "boss";
    this.boss.state = "intro"; this.boss.timer = 0;
    this.bossBanner = 120;
    this.audio?.sfx("win");
  }

  updateBoss() {
    const b = this.boss;
    b.timer++;
    if (this.crowdRoar > 0) this.crowdRoar--;
    for (const s of this.sparks) { s.x += s.vx; s.y += s.vy; s.vy += 0.25; s.life--; }
    this.sparks = this.sparks.filter((s) => s.life > 0);
    const homeX = WIDTH - 150;
    if (b.state !== "charge" && b.state !== "bossdefeat") b.x += (homeX - b.x) * 0.06;

    if (b.state === "intro") {
      if (b.timer > 70) this.nextBossAttack();
    } else if (b.state === "warn") {
      if (b.timer > 46) { b.state = b.cur.type; b.timer = 0; b.curHit = false; b.y = LANE_Y[b.cur.lanes[0]]; }
    } else if (b.state === "charge") {
      b.y = LANE_Y[b.cur.lanes[0]];
      b.x -= 36;
      this.checkBossHit(true);
      if (b.x < -280) { b.x = WIDTH + 200; this.finishBossAttack(); }
    } else if (b.state === "lasso" || b.state === "chancleta" || b.state === "fire" || b.state === "clown") {
      this.checkBossHit(b.cur.type === "fire" || b.cur.type === "clown");
      if (b.timer > (b.cur.type === "fire" ? 54 : b.cur.type === "clown" ? 52 : 46)) this.finishBossAttack();
    } else if (b.state === "recover") {
      if (b.timer > 26) {
        // A boss is BEATEN once its group of attacks is exhausted (next attack
        // is a different boss, or there are none left). Play its defeat gag.
        const curType = b.cur ? b.cur.type : null;
        const next = b.attacks[b.idx + 1];
        if (!next || next.type !== curType) this.startBossDefeat(curType, !next);
        else { b.idx++; this.nextBossAttack(); }
      }
    } else if (b.state === "bossdefeat") {
      if (b.timer > 180) {
        if (b.defeatLast) { this.startVictory(); return; }
        b.idx++;
        this.nextBossAttack();
      }
    }
  }

  // ~3s personality-based defeat gag for the boss just beaten.
  startBossDefeat(type, isLast) {
    const b = this.boss;
    b.state = "bossdefeat";
    b.defeatType = type || "charge";
    b.defeatLast = !!isLast;
    b.timer = 0;
    this.bossBanner = 0;
    this.audio?.sfx("win");
    const cols = ["#E91E63", "#FFEB3B", "#0067C6", "#4CAF50", "#fff"];
    for (let i = 0; i < 44; i++) { const a = Math.random() * 6.28, sp = 2 + Math.random() * 4; this.sparks.push({ x: WIDTH - 150, y: LANE_Y[4], vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 3, life: 46, color: cols[i % cols.length] }); }
  }

  nextBossAttack() {
    const b = this.boss;
    const a = { ...b.attacks[b.idx] };
    const R = (n) => Math.floor(Math.random() * n);
    if (a.type === "charge") a.lanes = [R(5)];
    else if (a.type === "lasso") { const s = R(4); a.lanes = [s, s + 1]; }
    else if (a.type === "chancleta") { const s = R(5); let t = R(5); while (t === s) t = R(5); a.lanes = [s, t]; }
    else if (a.type === "clown") { const s = R(5); let t = R(5); while (t === s) t = R(5); a.lanes = [s, t]; }
    else if (a.type === "fire") { const s = R(4); a.lanes = [s, s + 1]; }
    else { a.lanes = [R(5)]; }
    b.cur = a; b.curHit = false; b.state = "warn"; b.timer = 0;
    if (a.type !== b.lastType) { this.bossBanner = 100; b.lastType = a.type; }
    this.audio?.sfx("pop");
  }

  finishBossAttack() {
    const b = this.boss;
    if (!b.curHit) { b.done++; b.hp = Math.max(0, 1 - b.done / b.total); this.triggerCrowdRoar(); }
    b.state = "recover"; b.timer = 0;
  }

  // Crowd erupts each time you cleanly dodge a boss attack: bright roar pulse + cheer.
  triggerCrowdRoar() {
    this.crowdRoar = 20;
    this.audio?.sfx("cheer");
  }

  checkBossHit(jumpSaves) {
    const b = this.boss;
    if (this.invuln > 0 || b.curHit) return;
    if (!b.cur.lanes.includes(this.lane)) return;
    if (jumpSaves && this.jumpH >= JUMP_CLEAR) return;
    if (b.state === "charge") {
      if (Math.abs(b.x - PLAYER_X) < 120) { b.curHit = true; this.hit(); }
    } else if (b.timer > 12 && b.timer < 40) { b.curHit = true; this.hit(); }
  }

  startVictory() {
    this.mode = "victory";
    this.bossWin = true;
    this.vt = 0;
    this.shake = 16;
    this.bullDash = 0;
    this.planks = [];
    for (let i = 0; i < 12; i++) this.planks.push({ x: PLAYER_X + 150 + Math.random() * 90, y: this.laneYCur - 30 + Math.random() * 50, vx: 5 + Math.random() * 9, vy: -7 - Math.random() * 8, rot: Math.random() * 6, vr: (Math.random() - 0.5) * 0.5, w: 12 + Math.random() * 10, h: 44 + Math.random() * 34, life: 130 });
    this.confetti = [];
    this.fireworks = [];
    const cols = ["#ff3b3b", "#ffd54a", "#37b34a", "#3aa0ff", "#ff7ac2", "#ffffff"];
    for (let i = 0; i < 90; i++) this.confetti.push({ x: Math.random() * WIDTH, y: -20 - Math.random() * 260, vx: (Math.random() - 0.5) * 2.2, vy: 2 + Math.random() * 3.2, c: cols[i % cols.length], s: 5 + Math.random() * 6, rot: Math.random() * 6, vr: (Math.random() - 0.5) * 0.35 });
    this.audio?.sfx("win");
  }

  updateVictory() {
    this.frame++;
    this.vt++;
    this.shake = Math.max(0, 16 - this.vt * 0.5);
    this.bullDash += 6;
    for (const p of this.planks) { p.x += p.vx; p.y += p.vy; p.vy += 0.5; p.rot += p.vr; p.life--; }
    this.planks = this.planks.filter((p) => p.life > 0 && p.y < HEIGHT + 80);
    for (const c of this.confetti) { c.x += c.vx; c.y += c.vy; c.rot += c.vr; if (c.y > HEIGHT + 20) { c.y = -20; c.x = Math.random() * WIDTH; } }
    for (const d of this.dust) d.life--;
    this.dust = this.dust.filter((d) => d.life > 0);
    // Firework bursts light up the victory sky.
    if (this.vt < 190 && this.vt % 20 === 0) {
      const fx = 130 + Math.random() * (WIDTH - 260), fy = 60 + Math.random() * 120;
      const cols = ["#ff3b3b", "#ffd54a", "#37b34a", "#3aa0ff", "#ff7ac2", "#ffffff"];
      const col = cols[(Math.random() * cols.length) | 0];
      for (let i = 0; i < 26; i++) { const a = (i / 26) * 6.28, sp = 2 + Math.random() * 3; this.fireworks.push({ x: fx, y: fy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 40 + Math.random() * 20, color: col }); }
      this.audio?.sfx("pop");
    }
    for (const f of this.fireworks) { f.x += f.vx; f.y += f.vy; f.vy += 0.06; f.life--; }
    this.fireworks = this.fireworks.filter((f) => f.life > 0);
    if (this.vt > 210) this.endWin();
  }

  // ---------------- Render ----------------
  render() {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    ctx.save();
    if (this.mode === "victory" && this.shake > 0) ctx.translate((Math.random() - 0.5) * this.shake, (Math.random() - 0.5) * this.shake);
    this.drawBackground();
    this.drawGround();
    this.drawLanes();

    // Painter's order: build a list of coins, obstacles and the bull, sorted by
    // baseline Y so nearer (lower) items overlap farther ones.
    const items = [];
    for (const c of this.coinItems) {
      if (c.taken) continue;
      const sx = c.x - this.distance;
      if (sx < -30 || sx > WIDTH + 30) continue;
      items.push({ y: LANE_Y[c.lane] - 1000, fn: () => this.drawCoin(sx, LANE_Y[c.lane], LANE_SCALE[c.lane]) });
    }
    for (const o of this.obstacles) {
      if (o.done) continue;
      const sx = o.x - this.distance + Math.sin(this.frame * 0.05 + (o.ph || 0)) * 12;
      if (sx < -240 || sx > WIDTH + 60) continue;
      items.push({ y: LANE_Y[o.lane], fn: () => this.drawObstacle(o, sx, LANE_Y[o.lane], LANE_SCALE[o.lane]) });
    }
    const blink = this.invuln > 0 && Math.floor(this.frame / 4) % 2 === 0;
    items.push({ y: this.laneYCur + 0.5, fn: () => { if (!blink) this.drawBull(); } });
    items.sort((a, b) => a.y - b.y);
    for (const it of items) it.fn();

    for (const d of this.dust) { ctx.globalAlpha = Math.min(0.5, d.life / 30); ctx.fillStyle = "#c9a878"; ctx.beginPath(); ctx.arc(d.x, d.y, d.size, 0, Math.PI * 2); ctx.fill(); ctx.globalAlpha = 1; }
    for (const s of this.sparks) { ctx.globalAlpha = Math.min(1, s.life / 20); ctx.fillStyle = s.color; ctx.fillRect(Math.round(s.x), Math.round(s.y), 4, 4); ctx.globalAlpha = 1; }
    this.drawWind();
    if (this.mode === "run" && this.stampede) this.drawStampede();
    if (this.mode === "boss") this.drawBoss();
    if (this.mode === "victory") this.drawVictory();
    this.drawAtmosphere(Math.min(1, this.distance / COURSE_LEN));
    ctx.restore();
    if (this.hitFlash > 0) { ctx.fillStyle = `rgba(255,40,40,${this.hitFlash / 30})`; ctx.fillRect(0, 0, WIDTH, HEIGHT); }
    if (this.penalty > 0) {
      ctx.save();
      ctx.globalAlpha = Math.min(1, this.penalty / 20);
      ctx.font = "800 30px 'Baloo 2', system-ui"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.lineWidth = 5; ctx.strokeStyle = "#000"; ctx.fillStyle = "#ff4d4d";
      const py = this.laneYCur - 70 - (45 - this.penalty) * 1.2;
      const txt = `-${this.penaltyAmt} C$`;
      ctx.strokeText(txt, PLAYER_X, py); ctx.fillText(txt, PLAYER_X, py);
      ctx.restore();
    }
  }

  drawVictory() {
    const ctx = this.ctx;
    // Bright impact flash on the buck-off.
    if (this.vt < 10) { ctx.save(); ctx.globalAlpha = ((10 - this.vt) / 10) * 0.6; ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, WIDTH, HEIGHT); ctx.restore(); }
    for (const p of this.planks) {
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
      ctx.fillStyle = "#a9743e"; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.fillStyle = "rgba(0,0,0,0.18)"; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, 5);
      ctx.restore();
    }
    for (const c of this.confetti) {
      ctx.save(); ctx.translate(c.x, c.y); ctx.rotate(c.rot);
      ctx.fillStyle = c.c; ctx.fillRect(-c.s / 2, -c.s / 2, c.s, c.s * 0.6); ctx.restore();
    }
    // Fireworks.
    for (const f of this.fireworks || []) { ctx.globalAlpha = Math.min(1, f.life / 24); ctx.fillStyle = f.color; ctx.fillRect(Math.round(f.x), Math.round(f.y), 3, 3); }
    ctx.globalAlpha = 1;
    const drop = Math.min(1, this.vt / 26);
    const by = -50 + drop * 210;
    // Rotating golden sunburst behind the banner.
    ctx.save();
    ctx.translate(WIDTH / 2, by); ctx.rotate(this.frame * 0.01);
    ctx.globalAlpha = Math.min(0.4, this.vt / 55); ctx.fillStyle = "#ffd54a";
    for (let i = 0; i < 12; i++) { ctx.rotate(Math.PI / 6); ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(-24, -340); ctx.lineTo(24, -340); ctx.closePath(); ctx.fill(); }
    ctx.restore();
    // "¡CAMPEÓN!" banner with a slight victory pop.
    const pop = 1 + Math.max(0, (16 - this.vt) / 16) * 0.4;
    ctx.save();
    ctx.globalAlpha = Math.min(1, this.vt / 16);
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.translate(WIDTH / 2, by); ctx.scale(pop, pop);
    ctx.font = "900 66px 'Baloo 2', system-ui";
    ctx.lineWidth = 12; ctx.strokeStyle = "#000";
    ctx.strokeText("¡CAMPEÓN!", 0, 0);
    const gr = ctx.createLinearGradient(0, -40, 0, 40);
    gr.addColorStop(0, "#fff3b0"); gr.addColorStop(1, "#f7b301");
    ctx.fillStyle = gr; ctx.fillText("¡CAMPEÓN!", 0, 0);
    ctx.restore();
    // Subtitle fades in under the banner.
    if (this.vt > 22) {
      ctx.save();
      ctx.globalAlpha = Math.min(1, (this.vt - 22) / 20);
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.font = "800 26px 'Baloo 2', system-ui";
      ctx.lineWidth = 6; ctx.strokeStyle = "#000"; ctx.fillStyle = "#fff";
      ctx.strokeText("¡Derrotaste al Rodeo!", WIDTH / 2, by + 54);
      ctx.fillText("¡Derrotaste al Rodeo!", WIDTH / 2, by + 54);
      ctx.restore();
    }
  }

  drawBull() {
    const ctx = this.ctx;
    const s = LANE_SCALE[Math.round(Math.max(0, Math.min(4, this.lane)))];
    const dash = this.mode === "victory" ? Math.min(230, this.bullDash || 0) : 0;
    const cx = PLAYER_X + dash;
    // Ground shadow pinned to the lane FLOOR (level with obstacle shadows) and
    // shrinking as you jump — so it reads as staying on the ground.
    const sh = 1 - Math.min(0.55, this.jumpH / 240);
    ctx.fillStyle = `rgba(0,0,0,${0.32 * sh})`;
    ctx.beginPath(); ctx.ellipse(cx, this.laneYCur + 26 * s, 96 * s * sh, 14 * s * sh, 0, 0, Math.PI * 2); ctx.fill();
    const cy = this.laneYCur + 26 * s - this.jumpH;
    const tilt = Math.max(-0.14, Math.min(0.14, -this.vVert * 0.006));
    drawBullRider(ctx, { assets: this.assets, char: this.char, cx, cy, bullW: 220 * s, tilt, noShadow: true });
  }

  drawObstacle(o, sx, laneY, scale) {
    const ctx = this.ctx;
    const img = this.assets[o.sprite];
    const w = o.w * scale;
    const cx = sx + w / 2;
    ctx.fillStyle = "rgba(0,0,0,0.28)";
    ctx.beginPath(); ctx.ellipse(cx, laneY + 26 * scale, w * 0.5, 11 * scale, 0, 0, Math.PI * 2); ctx.fill();
    if (ready(img)) {
      const dh = img.naturalHeight * (w / img.naturalWidth);
      const hop = o.sprite === "obs_chicken" ? Math.abs(Math.sin(this.frame * 0.16 + (o.ph || 0))) * 22 * scale : 0;
      ctx.drawImage(img, sx, laneY + 26 * scale - dh - hop, w, dh);
    } else {
      ctx.fillStyle = "#c9a24a"; ctx.fillRect(sx, laneY - 60 * scale, w, 86 * scale);
    }
  }

  drawBackground() {
    const ctx = this.ctx;
    const DIRT_TOP = 300;
    const skyH = DIRT_TOP + 40;
    // Golden-hour build-up: sky warms toward sunset as you near the boss.
    const prog = Math.min(1, this.distance / COURSE_LEN);
    const k = prog * 0.85;
    const mix = (a, b) => `rgb(${Math.round(a[0] + (b[0] - a[0]) * k)},${Math.round(a[1] + (b[1] - a[1]) * k)},${Math.round(a[2] + (b[2] - a[2]) * k)})`;
    const g = ctx.createLinearGradient(0, 0, 0, skyH);
    g.addColorStop(0, mix([47, 127, 214], [255, 128, 66])); g.addColorStop(0.55, mix([120, 183, 238], [255, 176, 116])); g.addColorStop(1, mix([223, 240, 255], [255, 224, 180]));
    ctx.fillStyle = g; ctx.fillRect(0, 0, WIDTH, skyH);
    this.drawClouds();
    // Crowd stands: ONE image mirror-tiled so it reads as one continuous,
    // seamless crowd scrolling LEFT (opposite to the rightward ride).
    const stands = this.assets.rodeo_stands;
    if (ready(stands)) {
      const bandH = 270, bottom = DIRT_TOP + 18, top = bottom - bandH;
      const drawW = bandH * (stands.naturalWidth / stands.naturalHeight);
      const scroll = this.distance * 0.4;
      const startTile = Math.floor(scroll / drawW) - 1;
      for (let i = startTile; ; i++) {
        const x = i * drawW - scroll;
        if (x > WIDTH) break;
        // Integer x + 1px overlap so mirror-tiled crowd never shows a seam.
        const ix = Math.floor(x);
        const iw = Math.ceil(drawW) + 1;
        if (((i % 2) + 2) % 2 === 1) { ctx.save(); ctx.translate(ix + iw, 0); ctx.scale(-1, 1); ctx.drawImage(stands, 0, top, iw, bandH); ctx.restore(); }
        else ctx.drawImage(stands, ix, top, iw, bandH);
      }
      // haze blends the top of the stands into the sky for a smooth horizon
      const hz = ctx.createLinearGradient(0, top, 0, top + 44);
      hz.addColorStop(0, "rgba(223,240,255,0.6)"); hz.addColorStop(1, "rgba(223,240,255,0)");
      ctx.fillStyle = hz; ctx.fillRect(0, top, WIDTH, 44);
      this.drawFlags(bottom - bandH * 0.62);
    }
    // cowboy-hat sun rides along above the arena, following you left -> right
    this.drawSun();
  }

  drawAtmosphere(prog) {
    const ctx = this.ctx;
    // Ambient dust motes drifting across the arena for depth (lazy init).
    if (!this.motes) {
      this.motes = [];
      for (let i = 0; i < 38; i++) this.motes.push({ x: Math.random() * WIDTH, y: 120 + Math.random() * (HEIGHT - 170), r: 1 + Math.random() * 2.4, sp: 0.2 + Math.random() * 0.7, ph: Math.random() * 6.28 });
    }
    ctx.save();
    ctx.fillStyle = "#fff3d0";
    for (const m of this.motes) {
      m.x -= m.sp + this.speed * 0.02;
      if (m.x < -6) { m.x = WIDTH + 6; m.y = 120 + Math.random() * (HEIGHT - 170); }
      ctx.globalAlpha = 0.22 + 0.22 * Math.sin(this.frame * 0.06 + m.ph);
      ctx.beginPath(); ctx.arc(m.x, m.y + Math.sin(this.frame * 0.03 + m.ph) * 4, m.r, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    // Golden-hour colour grade that intensifies toward the boss.
    if (prog > 0.02) {
      ctx.globalCompositeOperation = "soft-light";
      ctx.fillStyle = `rgba(255,150,60,${0.4 * prog})`;
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
      ctx.globalCompositeOperation = "source-over";
    }
    // Vignette for cinematic depth.
    const vg = ctx.createRadialGradient(WIDTH / 2, HEIGHT * 0.52, HEIGHT * 0.3, WIDTH / 2, HEIGHT * 0.52, HEIGHT * 0.82);
    vg.addColorStop(0, "rgba(0,0,0,0)"); vg.addColorStop(1, `rgba(18,8,0,${0.26 + 0.16 * prog})`);
    ctx.fillStyle = vg; ctx.fillRect(0, 0, WIDTH, HEIGHT);
    // Crowd roar: warm screen-wide brightness pulse when you dodge a boss attack.
    if (this.crowdRoar > 0) {
      const a = this.crowdRoar / 20;
      ctx.globalCompositeOperation = "screen";
      ctx.fillStyle = `rgba(255,238,190,${0.3 * a})`;
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
      ctx.globalCompositeOperation = "source-over";
    }
    ctx.restore();
  }

  drawClouds() {
    const ctx = this.ctx;
    for (const c of this.clouds) {
      // drift LEFT: own speed + a slice of the run speed (parallax with the world)
      c.x -= c.spd + this.speed * 0.05;
      if (c.x < -260 * c.s) c.x = WIDTH + 140 + Math.random() * 220;
      const x = c.x, y = c.y, s = c.s;
      ctx.save();
      // shaded body gives the cloud 3D volume (bright top, cool underside)
      ctx.globalAlpha = 0.94;
      const grd = ctx.createLinearGradient(0, y - 24 * s, 0, y + 22 * s);
      grd.addColorStop(0, "#ffffff"); grd.addColorStop(1, "#c6d8ef");
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.ellipse(x, y, 42 * s, 21 * s, 0, 0, Math.PI * 2);
      ctx.ellipse(x + 32 * s, y + 6 * s, 27 * s, 15 * s, 0, 0, Math.PI * 2);
      ctx.ellipse(x - 32 * s, y + 7 * s, 25 * s, 14 * s, 0, 0, Math.PI * 2);
      ctx.ellipse(x + 5 * s, y - 12 * s, 24 * s, 16 * s, 0, 0, Math.PI * 2);
      ctx.fill();
      // top highlight for the lit crown of the cloud
      ctx.globalAlpha = 0.55; ctx.fillStyle = "#ffffff";
      ctx.beginPath(); ctx.ellipse(x + 2 * s, y - 9 * s, 30 * s, 11 * s, 0, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
  }

  drawSun() {
    const ctx = this.ctx;
    const img = this.assets.sun_cowboy;
    const prog = Math.min(1, this.distance / COURSE_LEN);
    const sx = WIDTH * 0.16 + prog * (WIDTH * 0.66) + Math.sin(this.frame * 0.02) * 14;
    const sz = 120;
    const sy = 50 + Math.sin(this.frame * 0.03) * 8;
    if (ready(img)) ctx.drawImage(img, sx - sz / 2, sy - sz / 2, sz, sz);
    else { ctx.fillStyle = "#ffd54a"; ctx.beginPath(); ctx.arc(sx, sy, sz * 0.32, 0, Math.PI * 2); ctx.fill(); }
  }

  drawFlags(baseY) {
    const ctx = this.ctx;
    const scroll = this.distance * 0.4;
    for (const f of this.flags) {
      const x = f.x - scroll;
      if (x < -40 || x > WIDTH + 40) continue;
      const wv = Math.sin(this.frame * 0.14 + f.ph);
      ctx.save();
      ctx.translate(x, baseY);
      ctx.strokeStyle = "#4b3826"; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, 58); ctx.stroke();
      const fw = 46, fh = 30;
      ctx.beginPath();
      ctx.moveTo(2, 2);
      ctx.quadraticCurveTo(2 + fw * 0.5, 2 + wv * 5, 2 + fw, 2 - wv * 4);
      ctx.lineTo(2 + fw, 2 - wv * 4 + fh);
      ctx.quadraticCurveTo(2 + fw * 0.5, 2 + wv * 5 + fh, 2, 2 + fh);
      ctx.closePath();
      if (f.fsln) { ctx.save(); ctx.clip(); ctx.fillStyle = "#d81e2c"; ctx.fillRect(0, -6, fw + 6, fh / 2 + 8); ctx.fillStyle = "#111"; ctx.fillRect(0, 2 + fh / 2 - 4, fw + 6, fh / 2 + 10); ctx.restore(); }
      else { ctx.save(); ctx.clip(); ctx.fillStyle = "#0067c6"; ctx.fillRect(0, -6, fw + 6, fh + 16); ctx.fillStyle = "#fff"; ctx.fillRect(0, 2 + fh / 3, fw + 6, fh / 3); ctx.restore(); }
      ctx.restore();
    }
  }

  drawGround() {
    const ctx = this.ctx;
    const gTop = 300;
    const img = this.assets.rodeo_ground;
    if (ready(img)) {
      const drawW = 300, drawH = HEIGHT - gTop + 10;
      const off = (this.distance) % drawW;
      // Integer x + 1px overlap so tiles never leave a hairline seam between them.
      const startX = Math.floor(-off - drawW);
      for (let x = startX; x < WIDTH + drawW; x += drawW) ctx.drawImage(img, x, gTop, drawW + 1, drawH);
    } else { ctx.fillStyle = "#b98a4e"; ctx.fillRect(0, gTop, WIDTH, HEIGHT - gTop); }
    ctx.fillStyle = "rgba(0,0,0,0.22)"; ctx.fillRect(0, gTop, WIDTH, 6);
    ctx.fillStyle = "#8a5a2c"; ctx.fillRect(0, gTop - 6, WIDTH, 6);
  }

  drawWind() {
    const ctx = this.ctx;
    for (const w of this.wind) {
      ctx.globalAlpha = w.a * Math.min(1, w.life / 40);
      ctx.strokeStyle = w.dirt ? "#d8b782" : "#ffffff";
      ctx.lineWidth = w.dirt ? 3 : 2;
      ctx.beginPath(); ctx.moveTo(w.x, w.y); ctx.lineTo(w.x + w.len, w.y); ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  drawCoin(x, baseY, scale = 1) {
    const ctx = this.ctx;
    const bob = Math.sin(this.frame * 0.14 + x * 0.03) * 2.5 * scale;
    // Sit the coin low inside its own lane band so its lane reads correctly
    // (drawing it high made coins look like they belonged to the lane above).
    const y = baseY - 20 * scale + bob;
    // Ground shadow anchors the coin to its exact lane so players can read depth.
    ctx.fillStyle = "rgba(0,0,0,0.22)";
    ctx.beginPath(); ctx.ellipse(x, baseY + 6 * scale, 16 * scale, 5.5 * scale, 0, 0, Math.PI * 2); ctx.fill();
    const spin = Math.abs(Math.sin(this.frame * 0.12 + x * 0.05));
    const rx = 20 * scale * (0.4 + spin * 0.6);
    ctx.save();
    // soft glow halo for visibility
    ctx.globalAlpha = 0.5; ctx.fillStyle = "rgba(255,214,74,0.9)";
    ctx.beginPath(); ctx.ellipse(x, y, rx + 6, 24 * scale, 0, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
    ctx.shadowColor = "rgba(255,210,74,0.7)"; ctx.shadowBlur = 10;
    ctx.fillStyle = "#f2b701"; ctx.beginPath(); ctx.ellipse(x, y, rx, 21 * scale, 0, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#ffe17a"; ctx.beginPath(); ctx.ellipse(x, y, rx * 0.7, 14 * scale, 0, 0, Math.PI * 2); ctx.fill();
    if (rx > 10) { ctx.fillStyle = "#9a6b00"; ctx.font = `800 ${Math.round(15 * scale)}px 'Baloo 2', system-ui`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("C$", x, y + 1); }
    ctx.restore();
  }

  drawLanes() {
    const ctx = this.ctx;
    const B = [332, 372, 412, 453, 495, 538]; // 5 lane boundaries + edges
    // alternating dirt tint so each lane reads as a distinct band
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = i % 2 === 0 ? "rgba(255,244,214,0.05)" : "rgba(50,30,10,0.10)";
      ctx.fillRect(0, B[i], WIDTH, B[i + 1] - B[i]);
    }
    // scrolling dashed separators convey speed + clearly split the lanes
    const dash = 36, gap = 26, period = dash + gap;
    const off = this.distance % period;
    ctx.lineWidth = 3;
    ctx.strokeStyle = "rgba(255,255,255,0.32)";
    for (let b = 1; b < 5; b++) {
      const y = B[b];
      ctx.beginPath();
      for (let x = -off; x < WIDTH; x += period) { ctx.moveTo(x, y); ctx.lineTo(x + dash, y); }
      ctx.stroke();
    }
    // strong highlight so you always know which lane you're in
    const li = Math.max(0, Math.min(4, this.lane));
    const top = B[li], h = B[li + 1] - top;
    const g = ctx.createLinearGradient(0, top, 0, top + h);
    g.addColorStop(0, "rgba(255,214,74,0.06)");
    g.addColorStop(0.5, "rgba(255,214,74,0.30)");
    g.addColorStop(1, "rgba(255,214,74,0.06)");
    ctx.fillStyle = g; ctx.fillRect(0, top, WIDTH, h);
    ctx.strokeStyle = "rgba(255,220,90,0.85)"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(0, top); ctx.lineTo(WIDTH, top); ctx.moveTo(0, top + h); ctx.lineTo(WIDTH, top + h); ctx.stroke();
    // left-edge chevron marker pointing at your current lane
    const my = top + h / 2, pulse = Math.sin(this.frame * 0.2) * 4;
    ctx.fillStyle = "#ffdc46";
    ctx.beginPath(); ctx.moveTo(6 + pulse, my - 12); ctx.lineTo(28 + pulse, my); ctx.lineTo(6 + pulse, my + 12); ctx.closePath(); ctx.fill();
  }

  drawBoss() {
    const ctx = this.ctx;
    const b = this.boss;
    if (b.state === "bossdefeat") { this.drawBossDefeat(b); return; }
    // danger-lane telegraph during warn + active windows
    if (b.cur && (b.state === "warn" || b.state === "lasso" || b.state === "chancleta" || b.state === "fire" || b.state === "clown")) {
      const flash = b.state === "warn" ? (0.22 + 0.24 * Math.sin(this.frame * 0.4)) : 0.26;
      for (const ln of b.cur.lanes) { ctx.fillStyle = `rgba(255,40,40,${flash})`; ctx.fillRect(0, LANE_B[ln], WIDTH, LANE_B[ln + 1] - LANE_B[ln]); }
    }
    // Clear incoming-attack telegraph: pulsing chevrons pointing into the danger lanes.
    if (b.state === "warn" && b.cur) {
      const a = 0.45 + 0.45 * Math.sin(this.frame * 0.4);
      ctx.save(); ctx.fillStyle = `rgba(255,48,48,${a})`;
      for (const ln of b.cur.lanes) {
        const yc = (LANE_B[ln] + LANE_B[ln + 1]) / 2;
        for (let c = 0; c < 3; c++) {
          const cxp = WIDTH - 24 - c * 30 - (this.frame % 24);
          ctx.beginPath(); ctx.moveTo(cxp, yc - 13); ctx.lineTo(cxp - 16, yc); ctx.lineTo(cxp, yc + 13); ctx.closePath(); ctx.fill();
        }
      }
      ctx.restore();
    }
    if (b.state === "lasso") this.drawLasso(b);
    else if (b.state === "chancleta") this.drawBottles(b);
    else if (b.state === "clown") this.drawClownPies(b);
    else if (b.state === "fire") this.drawFireRing(b);

    // Boss sprite depends on the current phase: the charging Toro Campeón bull,
    // El Ganadero (lasso) or La Borrachita (bottles) — each their own character.
    const type = b.cur ? b.cur.type : "charge";
    let img, bw, by, bob, flip = false;
    if (b.state === "charge") {
      img = this.assets.boss_bull; bw = 300; by = b.y; bob = 0;
    } else if (type === "lasso") {
      img = this.assets.boss_ganadero; bw = 205; by = LANE_Y[4]; bob = Math.sin(this.frame * 0.12) * 4;
    } else if (type === "chancleta") {
      img = this.assets.laborracha; bw = 155; by = LANE_Y[4]; bob = Math.sin(this.frame * 0.1) * 5; flip = true;
    } else if (type === "clown") {
      img = this.assets.boss_payaso; bw = 135; by = LANE_Y[4]; bob = Math.sin(this.frame * 0.16) * 6;
    } else {
      img = this.assets.boss_bull; bw = 300; by = LANE_Y[2]; bob = Math.sin(this.frame * 0.12) * 6;
    }
    ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.beginPath(); ctx.ellipse(b.x, by + 30, bw * 0.32, 16, 0, 0, Math.PI * 2); ctx.fill();
    if (ready(img)) {
      const bh = img.naturalHeight * (bw / img.naturalWidth);
      if (flip) {
        ctx.save(); ctx.translate(b.x, 0); ctx.scale(-1, 1);
        ctx.drawImage(img, -bw / 2, by + bob + 40 - bh, bw, bh);
        ctx.restore();
      } else {
        ctx.drawImage(img, b.x - bw / 2, by + bob + 40 - bh, bw, bh);
      }
    } else {
      ctx.fillStyle = "#3a2418"; ctx.beginPath(); ctx.ellipse(b.x, by, bw * 0.3, bw * 0.18, 0, 0, Math.PI * 2); ctx.fill();
    }
    // El Toro Mecánico crackles with electricity.
    if (b.state === "fire" && ready(img)) {
      ctx.save(); ctx.strokeStyle = "rgba(120,230,255,0.85)"; ctx.lineWidth = 2;
      for (let i = 0; i < 3; i++) {
        const a0 = this.frame * 0.3 + i * 2.1;
        ctx.beginPath(); ctx.moveTo(b.x - 44, by - 40);
        for (let s = 0; s <= 4; s++) ctx.lineTo(b.x - 44 + s * 24 + Math.sin(a0 + s) * 8, by - 40 - Math.cos(a0 + s * 1.5) * 10 - s * 4);
        ctx.stroke();
      }
      ctx.restore();
    }
    this.drawBossHP(b);
  }

  bossArt(type) {
    if (type === "lasso") return { img: this.assets.boss_ganadero, bw: 205, flip: false };
    if (type === "chancleta") return { img: this.assets.laborracha, bw: 155, flip: true };
    if (type === "clown") return { img: this.assets.boss_payaso, bw: 135, flip: false };
    return { img: this.assets.boss_bull, bw: 300, flip: false };
  }

  drawBossDefeat(b) {
    const ctx = this.ctx;
    const t = b.timer, p = Math.min(1, t / 180);
    const type = b.defeatType || "charge";
    const { img, bw, flip } = this.bossArt(type);
    const groundY = LANE_Y[4] + 40;
    let rot = 0, scale = 1, dx = 0, dy = 0;

    if (type === "charge") {                    // El Toro Campeón flops on his back, legs up, dizzy
      rot = Math.min(Math.PI * 0.92, t * 0.05) + Math.sin(this.frame * 0.2) * 0.05;
      dy = Math.sin(this.frame * 0.25) * 4;
    } else if (type === "lasso") {              // El Ganadero spins tangled in his own lasso
      rot = t * 0.3;
      scale = Math.max(0.55, 1 - p * 0.4);
      dy = p * 55;
    } else if (type === "chancleta") {          // La Borrachita sways drunk then topples
      if (t < 120) { rot = Math.sin(t * 0.16) * 0.4; dx = Math.sin(t * 0.16) * 16; }
      else { rot = 0.4 + Math.min(1.15, (t - 120) * 0.05); dy = Math.min(42, (t - 120) * 2); dx = 10; }
    } else if (type === "clown") {              // El Payaso spins then flops back with a pie in the face
      rot = t < 55 ? t * 0.28 : (55 * 0.28) + Math.min(1.05, (t - 55) * 0.04);
      dy = t > 55 ? Math.min(52, (t - 55) * 2) : 0;
    } else if (type === "fire") {               // El Toro Mecánico short-circuits, sparks and topples
      rot = Math.min(Math.PI * 0.5, t * 0.03);
      dy = t > 60 ? Math.min(44, (t - 60) * 1.7) : Math.sin(t * 0.7) * 3;
      if (t % 4 === 0) this.sparks.push({ x: (WIDTH - 150) + (Math.random() - 0.5) * 130, y: groundY - 70 - Math.random() * 40, vx: (Math.random() - 0.5) * 4, vy: -2 - Math.random() * 3, life: 22, color: Math.random() < 0.5 ? "#7fe9ff" : "#ffd54a" });
    }

    const cx = (WIDTH - 150) + dx;
    const bh = ready(img) ? img.naturalHeight * (bw / img.naturalWidth) : bw * 0.6;
    const cy = groundY - (bh * scale) / 2 + dy;

    ctx.fillStyle = "rgba(0,0,0,0.28)";
    ctx.beginPath(); ctx.ellipse(cx, groundY + 8, bw * 0.34 * scale, 14, 0, 0, Math.PI * 2); ctx.fill();

    if (ready(img)) {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);
      ctx.scale((flip ? -1 : 1) * scale, scale);
      ctx.drawImage(img, -bw / 2, -bh / 2, bw, bh);
      ctx.restore();
    }

    // spark confetti burst
    for (const s of this.sparks) { ctx.globalAlpha = Math.min(1, s.life / 30); ctx.fillStyle = s.color; ctx.fillRect(Math.round(s.x), Math.round(s.y), 5, 5); ctx.globalAlpha = 1; }

    // personality-specific gag props
    if (type === "charge") this.drawDizzyStars(cx, cy - bh * 0.32);
    else if (type === "lasso") this.drawGagLasso(cx, cy, bw);
    else if (type === "chancleta") this.drawHiccups(cx, cy - bh * 0.36);
    else if (type === "clown") this.drawPieSplat(cx, cy - bh * 0.22, t);
  }

  drawStar(x, y, r, color) {
    const ctx = this.ctx;
    ctx.save(); ctx.translate(x, y); ctx.fillStyle = color;
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const a = -Math.PI / 2 + i * (Math.PI * 2 / 5);
      ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      const a2 = a + Math.PI / 5;
      ctx.lineTo(Math.cos(a2) * r * 0.45, Math.sin(a2) * r * 0.45);
    }
    ctx.closePath(); ctx.fill(); ctx.restore();
  }

  drawDizzyStars(cx, cy) {
    for (let i = 0; i < 3; i++) {
      const a = this.frame * 0.13 + i * (Math.PI * 2 / 3);
      this.drawStar(cx + Math.cos(a) * 36, cy + Math.sin(a) * 13, 9, "#FFEB3B");
    }
  }

  drawGagLasso(cx, cy, bw) {
    const ctx = this.ctx;
    ctx.save(); ctx.strokeStyle = "#e6c78a"; ctx.lineWidth = 4;
    const wob = Math.sin(this.frame * 0.3) * 6;
    ctx.beginPath(); ctx.ellipse(cx, cy, bw * 0.42 + wob, bw * 0.3, this.frame * 0.1, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = "#b98a4e"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.ellipse(cx, cy + 12, bw * 0.3, bw * 0.2, -this.frame * 0.12, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }

  drawHiccups(cx, cy) {
    const ctx = this.ctx;
    for (let i = 0; i < 3; i++) {
      const ph = ((this.frame * 0.02) + i * 0.34) % 1;
      const x = cx + 22 + Math.sin(this.frame * 0.1 + i) * 10;
      const y = cy - ph * 52;
      ctx.globalAlpha = 1 - ph; ctx.fillStyle = "#bfe6ff";
      ctx.beginPath(); ctx.arc(x, y, 7 - ph * 3, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  drawPieSplat(cx, cy, t) {
    if (t < 30) return;
    const ctx = this.ctx;
    const r = 20 * Math.min(1, (t - 30) / 18);
    ctx.save(); ctx.translate(cx, cy);
    ctx.fillStyle = "#fff7e6";
    ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill();
    for (let i = 0; i < 6; i++) { const a = i * (Math.PI * 2 / 6); ctx.beginPath(); ctx.arc(Math.cos(a) * r, Math.sin(a) * r, r * 0.42, 0, Math.PI * 2); ctx.fill(); }
    ctx.fillStyle = "#ffd9ec"; ctx.beginPath(); ctx.arc(0, 0, r * 0.4, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  drawStampede() {
    const ctx = this.ctx;
    const s = this.stampede;
    if (s.phase === "warn") {
      const flash = 0.2 + 0.25 * Math.sin(this.frame * 0.5);
      const safeA = 0.18 + 0.2 * Math.sin(this.frame * 0.5);
      for (let ln = 0; ln < 5; ln++) {
        ctx.fillStyle = ln === s.safe ? `rgba(60,220,90,${safeA})` : `rgba(255,40,40,${flash})`;
        ctx.fillRect(0, LANE_B[ln], WIDTH, LANE_B[ln + 1] - LANE_B[ln]);
      }
      const sy = (LANE_B[s.safe] + LANE_B[s.safe + 1]) / 2;
      ctx.save();
      ctx.font = "800 26px 'Baloo 2', system-ui"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.lineWidth = 4; ctx.strokeStyle = "#063"; ctx.fillStyle = "#8CFF9E";
      ctx.strokeText("✓", PLAYER_X, sy); ctx.fillText("✓", PLAYER_X, sy);
      ctx.restore();
    } else {
      const drawn = [...s.bulls].sort((a, b) => LANE_Y[a.lane] - LANE_Y[b.lane]);
      for (const bl of drawn) {
        if (bl.x < -340 || bl.x > WIDTH + 340) continue;
        const img = bl.kind === 0 ? this.assets.stampede_bull_a : this.assets.stampede_bull_b;
        const sc = LANE_SCALE[bl.lane];
        const bw = 250 * sc;
        const baseY = LANE_Y[bl.lane] + 18;
        ctx.fillStyle = "rgba(0,0,0,0.28)"; ctx.beginPath(); ctx.ellipse(bl.x, baseY, bw * 0.34, 14 * sc, 0, 0, Math.PI * 2); ctx.fill();
        if (ready(img)) { const bh = img.naturalHeight * (bw / img.naturalWidth); ctx.drawImage(img, bl.x - bw / 2, baseY - bh, bw, bh); }
        else { ctx.fillStyle = "#5a3a22"; ctx.fillRect(bl.x - bw / 2, baseY - 60, bw, 60); }
      }
    }
    if (this.stampBanner > 0) {
      ctx.save();
      ctx.globalAlpha = Math.min(1, this.stampBanner / 22);
      ctx.font = "800 46px 'Baloo 2', system-ui"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.lineWidth = 7; ctx.strokeStyle = "#000"; ctx.fillStyle = "#ff3b3b";
      const txt = this.lang === "en" ? "STAMPEDE!" : "¡ESTAMPIDA!";
      ctx.strokeText(txt, WIDTH / 2, 120); ctx.fillText(txt, WIDTH / 2, 120);
      ctx.restore();
    }
  }

  drawLasso(b) {
    const ctx = this.ctx;
    const lanes = b.cur.lanes;
    const top = LANE_B[lanes[0]], bot = LANE_B[lanes[lanes.length - 1] + 1];
    const midY = (top + bot) / 2;
    const sweep = WIDTH - (b.timer / 46) * (WIDTH + 240);
    ctx.save();
    ctx.strokeStyle = "#b98a4e"; ctx.lineWidth = 4;
    ctx.beginPath(); ctx.moveTo(WIDTH + 40, top - 90); ctx.lineTo(sweep, midY); ctx.stroke();
    ctx.strokeStyle = "#e6c78a"; ctx.lineWidth = 7;
    ctx.beginPath(); ctx.ellipse(sweep, midY, 44, (bot - top) / 2 + 4, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }

  drawBottles(b) {
    const ctx = this.ctx;
    const prog = Math.min(1, b.timer / 46);
    const img = this.assets.beer;
    // La Borrachita hurls beer bottles from her spot (right) toward the player's
    // lane in a spinning arc.
    const fromX = WIDTH - 150;
    const toX = PLAYER_X + 20;
    for (const ln of b.cur.lanes) {
      const x = fromX - prog * (fromX - toX);
      const arc = Math.sin(prog * Math.PI) * 70;
      const y = LANE_Y[ln] - 24 - arc;
      const spin = b.timer * 0.45;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(spin);
      if (ready(img)) {
        const w = 48, h = img.naturalHeight * (w / img.naturalWidth);
        ctx.drawImage(img, -w / 2, -h / 2, w, h);
      } else {
        ctx.fillStyle = "#3a7d2c"; ctx.fillRect(-7, -20, 14, 34);
        ctx.fillStyle = "#2a5d1e"; ctx.fillRect(-3, -30, 6, 12);
        ctx.fillStyle = "#e8d59a"; ctx.fillRect(-7, -6, 14, 10);
      }
      ctx.restore();
    }
  }

  drawClownPies(b) {
    const ctx = this.ctx;
    const prog = Math.min(1, b.timer / 52);
    for (const ln of b.cur.lanes) {
      const x = WIDTH - 130 - prog * (WIDTH - PLAYER_X - 70);
      const y = LANE_Y[ln] - 10 - Math.sin(prog * Math.PI) * 14;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(b.timer * 0.3);
      ctx.fillStyle = "#cfc4b0"; ctx.beginPath(); ctx.ellipse(0, 6, 22, 8, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#fff7e6"; ctx.beginPath(); ctx.ellipse(0, 0, 20, 12, 0, Math.PI, 0); ctx.fill();
      ctx.fillStyle = "#ffd9ec"; ctx.beginPath(); ctx.arc(0, -3, 6, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
  }

  drawFireRing(b) {
    const ctx = this.ctx;
    const ln = b.cur.lanes[0];
    const x = WIDTH - (b.timer / 54) * (WIDTH + 200);
    const y = LANE_Y[ln] - 6;
    ctx.save();
    for (let r = 36; r > 12; r -= 8) { ctx.strokeStyle = `rgba(255,${110 + r * 3},20,0.9)`; ctx.lineWidth = 7; ctx.beginPath(); ctx.arc(x, y, r + Math.sin(this.frame * 0.5) * 2, 0, Math.PI * 2); ctx.stroke(); }
    ctx.restore();
  }

  drawBossHP(b) {
    const ctx = this.ctx;
    const w = 360, x = (WIDTH - w) / 2, y = 46;
    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,0.55)"; ctx.fillRect(x - 4, y - 4, w + 8, 26);
    ctx.fillStyle = "#3a0d0d"; ctx.fillRect(x, y, w, 18);
    ctx.fillStyle = "#ff2d2d"; ctx.fillRect(x, y, w * b.hp, 18);
    ctx.strokeStyle = "#ffd54a"; ctx.lineWidth = 2; ctx.strokeRect(x, y, w, 18);
    ctx.fillStyle = "#ffe17a"; ctx.font = "800 13px 'Baloo 2', system-ui"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("JEFE", WIDTH / 2, y + 9);
    ctx.restore();
    if (this.bossBanner > 0 && b.cur) {
      ctx.save();
      ctx.globalAlpha = Math.min(1, this.bossBanner / 30);
      ctx.font = "800 40px 'Baloo 2', system-ui"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.lineWidth = 6; ctx.strokeStyle = "#000"; ctx.fillStyle = "#FFEB3B";
      const txt = "¡" + (BOSS_NAMES[b.cur.type] || "JEFE") + "!";
      ctx.strokeText(txt, WIDTH / 2, 150); ctx.fillText(txt, WIDTH / 2, 150);
      ctx.restore();
    }
  }
}
